import { useState, useEffect, useCallback } from 'react';
import { GroupingMode, SparkTagDimensionSelection } from '../types/sparkTypes';

export type ViewMode = 'grid' | 'list';
export type SortField = 'name' | 'createdAt' | 'updatedAt';
export type SortOrder = 'asc' | 'desc';

export interface SortConfig {
  field: SortField;
  order: SortOrder;
}

interface BoardViewState {
  viewMode: ViewMode;
  sortConfig: SortConfig;
  filterLabel: string;
  unseenOnly: boolean;
  groupingMode: GroupingMode;
  tagFilterSelection: SparkTagDimensionSelection[];
}

const STORAGE_KEY_PREFIX = 'spark_board_view_';

const DEFAULT_STATE: BoardViewState = {
  viewMode: 'grid',
  sortConfig: { field: 'name', order: 'asc' },
  filterLabel: '',
  unseenOnly: false,
  groupingMode: 'none',
  tagFilterSelection: [],
};

/**
 * Hook to persist board view state (sort, filter, view mode, etc.) in sessionStorage.
 * State is keyed by creative type FQN so each board has independent settings.
 */
export function useBoardViewState(creativeTypeFqn: string) {
  const storageKey = `${STORAGE_KEY_PREFIX}${creativeTypeFqn}`;

  // Initialize from sessionStorage or defaults
  const [state, setState] = useState<BoardViewState>(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<BoardViewState>;
        // Merge with defaults to handle missing fields from older stored state
        return { ...DEFAULT_STATE, ...parsed };
      }
    } catch {
      // Ignore parse errors
    }
    return DEFAULT_STATE;
  });

  // Persist to sessionStorage when state changes
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // Ignore storage errors (quota exceeded, etc.)
    }
  }, [storageKey, state]);

  // Individual setters that update both local state and trigger persistence
  const setViewMode = useCallback((viewMode: ViewMode) => {
    setState(prev => ({ ...prev, viewMode }));
  }, []);

  const setSortConfig = useCallback((sortConfig: SortConfig) => {
    setState(prev => ({ ...prev, sortConfig }));
  }, []);

  const setFilterLabel = useCallback((filterLabel: string) => {
    setState(prev => ({ ...prev, filterLabel }));
  }, []);

  const setUnseenOnly = useCallback((update: boolean | ((prev: boolean) => boolean)) => {
    setState(prev => ({
      ...prev,
      unseenOnly: typeof update === 'function' ? update(prev.unseenOnly) : update,
    }));
  }, []);

  const setGroupingMode = useCallback((groupingMode: GroupingMode) => {
    setState(prev => ({ ...prev, groupingMode }));
  }, []);

  const setTagFilterSelection = useCallback((
    update: SparkTagDimensionSelection[] | ((prev: SparkTagDimensionSelection[]) => SparkTagDimensionSelection[])
  ) => {
    setState(prev => ({
      ...prev,
      tagFilterSelection: typeof update === 'function' ? update(prev.tagFilterSelection) : update,
    }));
  }, []);

  return {
    viewMode: state.viewMode,
    sortConfig: state.sortConfig,
    filterLabel: state.filterLabel,
    unseenOnly: state.unseenOnly,
    groupingMode: state.groupingMode,
    tagFilterSelection: state.tagFilterSelection,
    setViewMode,
    setSortConfig,
    setFilterLabel,
    setUnseenOnly,
    setGroupingMode,
    setTagFilterSelection,
  };
}
